import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/modules/app';
import KBMediaModel from '@/models/KnowledgeBase/KBMedia';
import KBMediaListItem from '@/models/KnowledgeBase/KBMediaListItem';
import CreateKBMediaRequest from '@/models/KnowledgeBase/CreateKBMediaRequest';

export interface KBMediaList {
  items: KBMediaListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateKBMediaResponse {
  mediaId: string;
  uploadUrl: string;
  expiresAt: string;
}

export interface ConfirmKBMediaUploadRequest {
  generation: number;
  width?: number;
  height?: number;
  isNewVersion: boolean;
}

export interface UpdateKBMediaMetadataRequest {
  tags: string[];
  altText: string;
}

export interface ReplaceKBMediaFileRequest {
  fileSize: number;
  fileType: string;
}

export interface ReplaceKBMediaFileResponse {
  uploadUrl: string;
  expiresAt: string;
}

export interface RepublishMediaContentResponse {
  success: boolean;
  contentCount: number;
  message: string;
}

export interface ISysKBMediaState {
  loadingMediaList: boolean;
  mediaList: KBMediaList | null;
  loadingMedia: boolean;
  currentMedia: KBMediaModel | null;
  loadingDialog: boolean;
  uploadProgress: number;
}

@Module({ dynamic: true, store, name: 'SysKBMedia', namespaced: true })
class SysKBMedia extends VuexModule implements ISysKBMediaState {

  public loadingMediaList = false;
  public mediaList: KBMediaList | null = null;
  public loadingMedia = false;
  public currentMedia: KBMediaModel | null = null;
  public loadingDialog = false;
  public uploadProgress = 0;

  // Media List Actions
  @Action
  async getMediaList(params?: {
    searchTerm?: string;
    mediaType?: number;
    tag?: string;
    sortBy?: string;
    sortOrder?: string;
    limit?: number;
    offset?: number;
  }) {
    this.setLoadingMediaList(true);
    const queryParams = new URLSearchParams();
    if (params?.searchTerm) queryParams.append('searchTerm', params.searchTerm);
    if (params?.mediaType !== undefined) queryParams.append('mediaType', params.mediaType.toString());
    if (params?.tag) queryParams.append('tag', params.tag);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());

    const url = '/api/v1/sys/kb/media' + (queryParams.toString() ? '?' + queryParams.toString() : '');

    return ajax.get(url)
      .then(response => {
        this.context.commit('setMediaList', response.data);
        this.setLoadingMediaList(false);
        return response.data;
      })
      .catch(error => {
        AppModule.handleApiError(error);
        this.setLoadingMediaList(false);
        throw error;
      });
  }

  // Get single media
  @Action
  async getMedia(mediaId: string) {
    this.setLoadingMedia(true);
    return ajax.get(`/api/v1/sys/kb/media/${mediaId}`)
      .then(response => {
        this.context.commit('setCurrentMedia', response.data);
        this.setLoadingMedia(false);
        return response.data;
      })
      .catch(error => {
        AppModule.handleApiError(error);
        this.setLoadingMedia(false);
        throw error;
      });
  }

  // Create media (get signed upload URL)
  @Action
  async createMedia(request: CreateKBMediaRequest): Promise<CreateKBMediaResponse> {
    this.setLoadingDialog(true);
    return ajax.post('/api/v1/sys/kb/media', request)
      .then(response => {
        this.setLoadingDialog(false);
        return response.data;
      })
      .catch(error => {
        AppModule.handleApiError(error);
        this.setLoadingDialog(false);
        throw error;
      });
  }

  // Upload file to signed URL
  @Action
  async uploadFile(params: { uploadUrl: string; file: File }): Promise<{ generation: string }> {
    this.setUploadProgress(0);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          this.setUploadProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          // GCS returns generation in x-goog-generation header
          const generation = xhr.getResponseHeader('x-goog-generation') || '0';
          this.setUploadProgress(100);
          resolve({ generation });
        } else {
          this.setUploadProgress(0);
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        this.setUploadProgress(0);
        reject(new Error('Upload failed'));
      });

      xhr.open('PUT', params.uploadUrl);
      xhr.setRequestHeader('Content-Type', params.file.type);
      xhr.send(params.file);
    });
  }

  // Confirm upload
  @Action
  async confirmUpload(params: { mediaId: string; request: ConfirmKBMediaUploadRequest }) {
    this.setLoadingDialog(true);
    return ajax.put(`/api/v1/sys/kb/media/${params.mediaId}/confirm`, params.request)
      .then(response => {
        this.setLoadingDialog(false);
        return response.data;
      })
      .catch(error => {
        AppModule.handleApiError(error);
        this.setLoadingDialog(false);
        throw error;
      });
  }

  // Update metadata
  @Action
  async updateMetadata(params: { mediaId: string; request: UpdateKBMediaMetadataRequest }) {
    this.setLoadingDialog(true);
    return ajax.put(`/api/v1/sys/kb/media/${params.mediaId}`, params.request)
      .then(response => {
        this.setLoadingDialog(false);
        // Refresh current media
        this.getMedia(params.mediaId);
        return response.data;
      })
      .catch(error => {
        AppModule.handleApiError(error);
        this.setLoadingDialog(false);
        throw error;
      });
  }

  // Replace file
  @Action
  async replaceFile(params: { mediaId: string; request: ReplaceKBMediaFileRequest }): Promise<ReplaceKBMediaFileResponse> {
    this.setLoadingDialog(true);
    return ajax.put(`/api/v1/sys/kb/media/${params.mediaId}/file`, params.request)
      .then(response => {
        this.setLoadingDialog(false);
        return response.data;
      })
      .catch(error => {
        AppModule.handleApiError(error);
        this.setLoadingDialog(false);
        throw error;
      });
  }

  // Archive media
  @Action
  async archiveMedia(mediaId: string) {
    this.setLoadingDialog(true);
    return ajax.delete(`/api/v1/sys/kb/media/${mediaId}`)
      .then(response => {
        this.setLoadingDialog(false);
        // Refresh media list
        this.getMediaList();
        return response.data;
      })
      .catch(error => {
        AppModule.handleApiError(error);
        this.setLoadingDialog(false);
        throw error;
      });
  }

  // Republish content using media
  @Action
  async republishContent(mediaId: string): Promise<RepublishMediaContentResponse> {
    this.setLoadingDialog(true);
    return ajax.post(`/api/v1/sys/kb/media/${mediaId}/republish`)
      .then(response => {
        this.setLoadingDialog(false);
        // Refresh media to clear needsRepublish flag
        this.getMedia(mediaId);
        return response.data;
      })
      .catch(error => {
        AppModule.handleApiError(error);
        this.setLoadingDialog(false);
        throw error;
      });
  }

  // Utility Actions
  @Action
  cleanState() {
    this.context.commit('resetState');
  }

  // Mutations
  @Mutation
  resetState() {
    this.mediaList = null;
    this.currentMedia = null;
    this.loadingMediaList = false;
    this.loadingMedia = false;
    this.loadingDialog = false;
    this.uploadProgress = 0;
  }

  @Mutation
  private setLoadingMediaList(loading: boolean) {
    this.loadingMediaList = loading;
  }

  @Mutation
  private setMediaList(list: KBMediaList) {
    this.mediaList = list;
  }

  @Mutation
  private setLoadingMedia(loading: boolean) {
    this.loadingMedia = loading;
  }

  @Mutation
  private setCurrentMedia(media: KBMediaModel) {
    this.currentMedia = media;
  }

  @Mutation
  private setLoadingDialog(loading: boolean) {
    this.loadingDialog = loading;
  }

  @Mutation
  private setUploadProgress(progress: number) {
    this.uploadProgress = progress;
  }

  @Mutation
  private setSearchResults(results: any) {
    // Future: implement media search results if needed
  }
}

export const SysKBMediaModule = getModule(SysKBMedia);
